import { useState, type FormEvent } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { procitajGreske, type PorukeGresaka } from "@/lib/greske";
import { recordOptions } from "@/lib/employeeRecordLabels";
import { dohvatiTipoveDokumenata, ucitajDokument, type TipDokumenta } from "@/lib/dokumenti";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/design-system";
import "./EmployeeRecordCreate.css";

type Kind = "training" | "health" | "equipment" | "qualification" | "permit";
interface Field { key: string; label: string; type?: "date"; required?: boolean; maxLength?: number; options?: [string, string][]; more?: boolean }
const definitions = {
  permit: { permission: "dozvola", path: "dozvole", button: "Dodaj dozvolu", success: "Dozvola upisana.", codes: ["single_residence_work_permit", "eea_residence_registration", "visa_d"], start: "datum_izdavanja", end: "datum_isteka" },
  training: { permission: "znr", path: "osposobljavanja", button: "Dodaj osposobljavanje", success: "Osposobljavanje upisano.", codes: ["occupational_safety_training"], start: "datum_osposobljavanja", end: "" },
  health: { permission: "zdravlje", path: "zdravstvene-sposobnosti", button: "Dodaj liječnički pregled", success: "Liječnički pregled upisan.", codes: ["ra2_medical_fitness", "pur_medical_certificate"], start: "datum_pregleda", end: "datum_sljedeceg_pregleda" },
  equipment: { permission: "znr", path: "zastitna-oprema", button: "Dodaj zaštitnu opremu", success: "Zaštitna oprema upisana.", codes: ["ppe_handover"], start: "datum_zaduzenja", end: "rok_zamjene" },
  qualification: { permission: "radnik", path: "kvalifikacije", button: "Dodaj kvalifikaciju", success: "Kvalifikacija upisana.", codes: ["machinery_crane_licence", "a1_1_croatian_certificate"], start: "datum_izdavanja", end: "datum_isteka" },
} satisfies Record<Kind, object>;

function localToday() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function initialValues(kind: Kind): Record<string, string> {
  if (kind === "training") return { datum_osposobljavanja: localToday(), status: "osposobljen", dogadaj_okidac: recordOptions("osposobljavanje_znr", "dogadaj_okidac")[0]?.[0] ?? "" };
  if (kind === "qualification") return {};
  return { [definitions[kind].start]: localToday() };
}

function fieldsFor(kind: Kind, values: Record<string, string>): Field[] {
  switch (kind) {
    case "permit": return [
      { key: "tip", label: "Tip", required: true, options: recordOptions("dozvola", "tip") },
      { key: "broj_dozvole", label: "Broj", maxLength: 120 },
      { key: "datum_izdavanja", label: "Izdana", type: "date", required: true },
      { key: "datum_isteka", label: "Ističe", type: "date", required: true },
    ];
    case "training": return [
      { key: "vrsta_izvorni_tekst", label: "Vrsta osposobljavanja", required: true },
      { key: "datum_osposobljavanja", label: "Datum osposobljavanja", type: "date", required: values.status === "osposobljen" },
      { key: "napomena", label: "Napomena" },
      { key: "status", label: "Status", required: true, options: recordOptions("osposobljavanje_znr", "status"), more: true },
      { key: "dogadaj_okidac", label: "Događaj okidač", required: true, options: recordOptions("osposobljavanje_znr", "dogadaj_okidac"), more: true },
      ...(values.status === "pod_nadzorom" ? [{ key: "nadzor_pocetak", label: "Početak nadzora", type: "date" as const, required: true, more: true }] : []),
    ];
    case "health": return [
      { key: "ocjena", label: "Ocjena", required: true, options: recordOptions("zdravstvena_sposobnost", "ocjena") },
      { key: "vrsta_pregleda", label: "Vrsta pregleda", required: true, options: recordOptions("zdravstvena_sposobnost", "vrsta_pregleda") },
      { key: "datum_pregleda", label: "Datum pregleda", type: "date", required: true },
      { key: "datum_sljedeceg_pregleda", label: "Vrijedi do", type: "date" },
    ];
    case "equipment": return [
      { key: "vrsta_izvorni_tekst", label: "Vrsta opreme", required: true },
      { key: "datum_zaduzenja", label: "Datum zaduženja", type: "date", required: true },
      { key: "velicina", label: "Veličina", maxLength: 40 },
      { key: "rok_zamjene", label: "Rok zamjene", type: "date" },
    ];
    case "qualification": return [
      { key: "tip", label: "Tip kvalifikacije", required: true, options: recordOptions("kvalifikacija", "tip") },
      { key: "naziv", label: "Naziv", required: true, maxLength: 200 },
      { key: "izdavatelj", label: "Izdavatelj", maxLength: 200 },
      { key: "datum_izdavanja", label: "Datum izdavanja", type: "date" },
      { key: "datum_isteka", label: "Vrijedi do", type: "date" },
    ];
  }
}

/** Uses the existing create endpoints and their existing permission boundaries. */
export function EmployeeRecordCreate({ kind, radnikId, onSaved, permitType, buttonLabel, prefill, initiallyOpen = false, onCreated, secondaryActions = false }: { kind: Kind; radnikId: string; onSaved: () => void; permitType?: string; buttonLabel?: string; prefill?: Record<string, string>; initiallyOpen?: boolean; onCreated?: (id: string) => void; secondaryActions?: boolean }) {
  const { can } = useAuth();
  const definition = definitions[kind];
  const [open, setOpen] = useState(initiallyOpen);
  const [success, setSuccess] = useState(false);
  const [values, setValues] = useState<Record<string, string>>(() => ({ ...initialValues(kind), ...prefill }));
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<PorukeGresaka>({ polja: {}, opcenito: [] });
  const [types, setTypes] = useState<TipDokumenta[] | null>(null);
  const [attachmentOpen, setAttachmentOpen] = useState(false);
  const [attachmentError, setAttachmentError] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [file, setFile] = useState<File | null>(null);
  // Upload and record creation are separate operations. Keep the upload across retries.
  const [archiveId, setArchiveId] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [permanent, setPermanent] = useState(false);
  const fields = fieldsFor(kind, values).map(field => kind === "permit" && permitType && field.key === "tip" ? { ...field, options: field.options?.filter(([key]) => key === permitType) } : field);
  const attachmentAllowed = can("dokument", "create") && can("dokument", "read") && (kind !== "health" || can("zdravlje", "update"));
  if (!can(definition.permission, "create")) return null;

  async function openAttachment() {
    setAttachmentOpen(true);
    setAttachmentError("");
    try {
      const available = (await dohvatiTipoveDokumenata()).filter(type => definition.codes.includes(type.code) && type.active_version && type.active_version.requirement_level !== "reference_only" && (definition.end || !type.active_version.expiry_required));
      setTypes(available);
      setDocumentType(available[0]?.code ?? "");
    } catch { setAttachmentError("Vrste dokumenata se ne mogu učitati. Pokušajte ponovno."); }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || !can(definition.permission, "create")) return;
    const fieldErrors: Record<string, string> = {};
    const body: Record<string, string | boolean | null> = {};
    fields.forEach(field => {
      const value = values[field.key]?.trim() || "";
      if (field.required && !value) fieldErrors[field.key] = "Unesite obvezni podatak.";
      body[field.key] = value || null;
    });
    if (kind === "qualification") {
      body.trajno = permanent;
      if (permanent) body.datum_isteka = null;
    }
    const start = values[definition.start];
    const end = kind === "qualification" && permanent ? undefined : values[definition.end];
    if (start && end && end < start) fieldErrors[definition.end] = "Završni datum ne može biti prije početnog.";
    if (kind === "permit" && start && end && end === start) fieldErrors[definition.end] = "Datum isteka mora biti nakon datuma izdavanja.";
    const selectedType = types?.find(type => type.code === documentType);
    if (file && !archiveId && (!attachmentAllowed || !selectedType)) fieldErrors.prilog = "Odaberite vrstu dokumenta.";
    if (file && !archiveId && selectedType?.active_version?.expiry_required && !(kind === "qualification" && permanent) && !values[definition.end]) fieldErrors[definition.end || "prilog"] = "Za ovaj prilog unesite datum isteka.";
    if (Object.keys(fieldErrors).length) { setErrors({ polja: fieldErrors, opcenito: [] }); return; }
    setBusy(true);
    setErrors({ polja: {}, opcenito: [] });
    try {
      let uploadedArchiveId = archiveId;
      let uploadedDocumentId = documentId;
      if (file && attachmentAllowed) {
        if (!uploadedArchiveId) {
          const uploaded = await ucitajDokument(radnikId, { documentTypeCode: documentType, naziv: file.name, datoteka: file, issuedOn: values[definition.start] || undefined, expiresOn: end || undefined, ...(kind === "qualification" && permanent ? { qualificationTrajno: true } : {}) });
          uploadedArchiveId = uploaded.id;
          setArchiveId(uploaded.id);
        }
        if (!uploadedDocumentId) {
          const response = await api.get<{ dokument_id: string }[]>(`/radnici/${radnikId}/documents/${uploadedArchiveId}/files`);
          uploadedDocumentId = response.data[0]?.dokument_id ?? null;
          if (!uploadedDocumentId) throw new Error("Missing uploaded file");
          setDocumentId(uploadedDocumentId);
        }
        body[kind === "equipment" ? "potvrda_dokument_id" : "dokument_id"] = uploadedDocumentId;
      }
      const response = await api.post<{ id: string }>(`/radnici/${radnikId}/${definition.path}`, body);
      onCreated?.(response.data.id);
      setOpen(false);
      setSuccess(true);
      onSaved();
    } catch (error) { setErrors(procitajGreske(error)); }
    finally { setBusy(false); }
  }

  function renderField(field: Field) {
    if (kind === "qualification" && permanent && field.key === "datum_isteka") return null;
    return <label key={field.key} className="flex min-w-0 flex-col gap-1">{field.label}
      {field.options ? <Select className="min-h-11" required={field.required} value={values[field.key] ?? ""} aria-invalid={!!errors.polja[field.key]} onChange={event => setValues({ ...values, [field.key]: event.target.value })}><option value="">Odaberite</option>{field.options.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</Select>
        : <Input className="min-h-11" type={field.type ?? "text"} required={field.required} maxLength={field.maxLength} min={field.key === definition.end ? values[definition.start] : undefined} value={values[field.key] ?? ""} aria-invalid={!!errors.polja[field.key]} onChange={event => setValues({ ...values, [field.key]: event.target.value })} />}
      {errors.polja[field.key] && <span role="alert">{errors.polja[field.key]}</span>}
    </label>;
  }

  return <div className="avm-record-create space-y-3">
    <Button type="button" variant={secondaryActions ? "secondary" : "default"} className="min-h-11" aria-expanded={open} disabled={busy} onClick={() => {
      if (open) { setOpen(false); return; }
      setValues({ ...initialValues(kind), ...prefill, ...(kind === "permit" && permitType ? { tip: permitType } : {}) }); setSuccess(false); setErrors({ polja: {}, opcenito: [] });
      setFile(null); setArchiveId(null); setDocumentId(null); setPermanent(false); setAttachmentOpen(false); setTypes(null); setAttachmentError(""); setOpen(true);
    }}>{buttonLabel ?? definition.button}</Button>
    {success && <p role="status">{definition.success}{archiveId && <a className="ml-2 inline-flex min-h-11 items-center underline" href={`/zaposlenici/${radnikId}?tab=dokumenti&document=${encodeURIComponent(archiveId)}`}>Otvori priloženi dokument</a>}</p>}
    {open && <form aria-label={buttonLabel ?? definition.button} onSubmit={event => void submit(event)} className="rounded-[var(--radius-card)] border border-[var(--color-border)] p-4 space-y-4">
      {errors.opcenito.map(message => <p role="alert" key={message}>{message}</p>)}
      {archiveId && <p>Prilog je učitan u dokumente radnika. Pri ponovnom pokušaju koristit će se isti prilog.</p>}
      <fieldset disabled={busy} className="space-y-4">
        {kind === "qualification" && <label className="flex min-h-11 items-center gap-2"><input type="checkbox" checked={permanent} onChange={event => { setPermanent(event.target.checked); setValues({ ...values, datum_isteka: "" }); }} />Ne istječe (trajno)</label>}
        <div className="grid gap-4 sm:grid-cols-2">{fields.filter(field => !field.more).map(renderField)}</div>
        {kind === "training" && <details><summary className="min-h-11 cursor-pointer py-3">Više</summary><div className="grid gap-4 sm:grid-cols-2">{fields.filter(field => field.more).map(renderField)}</div></details>}
        {attachmentAllowed && <div className="space-y-3">
          {!attachmentOpen ? <Button type="button" variant="secondary" className="min-h-11" onClick={() => void openAttachment()}>Priloži dokument (neobvezno)</Button> : <>
            {attachmentError && <div role="alert">{attachmentError}<Button type="button" variant="secondary" onClick={() => void openAttachment()}>Pokušaj ponovno</Button></div>}
            {types === null && !attachmentError && <p>Učitavanje vrsta dokumenata…</p>}
            {types?.length === 0 && <p>Za ovaj zapis trenutačno nema dostupne vrste priloga.</p>}
            {!!types?.length && <div className="grid gap-4 sm:grid-cols-2"><label className="flex flex-col gap-1">Vrsta dokumenta<Select className="min-h-11" disabled={!!archiveId} value={documentType} onChange={event => setDocumentType(event.target.value)}>{types.map(type => <option key={type.code} value={type.code}>{type.active_version?.name_hr}</option>)}</Select></label><label className="flex min-w-0 flex-col gap-1">Prilog (PDF, JPEG ili PNG, do 15 MB)<Input className="min-h-11" type="file" accept="application/pdf,image/jpeg,image/png" disabled={!!archiveId} onChange={event => {
              const selected = event.target.files?.[0] ?? null;
              if (selected && (selected.size > 15 * 1024 * 1024 || !["application/pdf", "image/jpeg", "image/png"].includes(selected.type))) { setAttachmentError("Odaberite PDF, JPEG ili PNG do 15 MB."); event.target.value = ""; setFile(null); return; }
              setAttachmentError(""); setFile(selected);
            }} /></label></div>}
            {errors.polja.prilog && <p role="alert">{errors.polja.prilog}</p>}
          </>}
        </div>}
        <div className="flex flex-wrap gap-2"><Button variant={secondaryActions ? "secondary" : "default"} className="min-h-11" type="submit">{busy ? "Spremanje…" : "Upiši"}</Button><Button className="min-h-11" type="button" variant="secondary" onClick={() => setOpen(false)}>Odustani</Button></div>
      </fieldset>
    </form>}
  </div>;
}
